import {
  ArrowRight,
  ExternalLink,
  FlaskConical,
  BookOpen,
  Github,
  FileText,
  Sparkles,
  Shield,
  Bot,
  Lock,
  Cpu,
  Network,
  Mail,
  Globe,
  Code2,
} from 'lucide-react';
import { TrinetraLoop3 } from '@nirvikailabs/brand/icons';

/**
 * research.nirvik.ai — Nirvik AI Labs research landing.
 *
 * Tone: rigorous but accessible. Practitioner-first, not academic.
 * We publish what we learn, build what we research, share it all.
 *
 * Draws motivation directly from the April 7, 2026 inflection point
 * on nirvik.ai — the same founding story, pivoted to the question:
 * what does defense need to look like now that offense has AI?
 *
 * Visual identity: saffron leads on deep indigo (intellectual curiosity,
 * warmth, the research flame). Matches the Research card accent on nirvik.ai.
 * Halos in saffron/turmeric. Same dark-mode-first treatment.
 *
 * Key sections:
 *   1. Nav
 *   2. Hero — "We research the frontier. We publish what we learn."
 *   3. Why this research matters — the inflection point
 *   4. Research focus areas (4 pillars)
 *   5. Publications (pre-publication / coming soon)
 *   6. Open source
 *   7. Writing / blog
 *   8. CTA — follow the research
 *   9. Tricolor strip + footer
 */

const CONTACT_EMAIL = 'research@nirvik.ai';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">

      {/* ─── Ambient halos ─── */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-halo-turmeric opacity-80" />
        <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-halo-indigo" />
      </div>

      {/* ─── Nav ─── */}
      <nav className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5 max-w-6xl mx-auto gap-4">
        <a href="/" className="flex items-center gap-2.5 shrink-0">
          <FlaskConical className="h-6 w-6 text-saffron" />
          <span className="font-display text-lg sm:text-xl font-semibold tracking-wide">
            Nirvik <span className="text-saffron">Research</span>
          </span>
        </a>
        <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="#focus" className="hover:text-foreground transition-colors">Focus areas</a>
          <a href="#publications" className="hover:text-foreground transition-colors">Publications</a>
          <a href="#open-source" className="hover:text-foreground transition-colors">Open source</a>
          <a href="#writing" className="hover:text-foreground transition-colors">Writing</a>
          <a href="https://nirvik.ai" className="hover:text-foreground transition-colors">Nirvik AI Labs</a>
        </div>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="text-sm font-medium text-saffron hover:text-foreground transition-colors inline-flex items-center gap-1.5 shrink-0"
        >
          Follow the research
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative z-10 px-5 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-28 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-saffron/10 border border-saffron/25 text-saffron px-4 py-1.5 rounded-full text-sm font-medium mb-10">
          <Sparkles className="h-4 w-4" />
          Open research. Practical defense. Built for the age of agentic AI.
        </div>

        <div className="flex justify-center mb-10">
          <div className="relative">
            <div className="h-24 w-24 rounded-[28px] bg-gradient-to-br from-saffron/80 to-primary/70 flex items-center justify-center shadow-[0_25px_60px_-15px_color-mix(in_oklch,var(--saffron)_40%,transparent)]">
              <FlaskConical className="h-12 w-12 text-primary-foreground" />
            </div>
            <div className="absolute -inset-4 rounded-[32px] bg-saffron/15 blur-2xl -z-10" />
          </div>
        </div>

        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight leading-[1.04] mb-6 bg-gradient-to-br from-foreground via-saffron to-primary-strong bg-clip-text text-transparent">
          Nirvik Research
        </h1>

        <p className="text-lg sm:text-2xl text-foreground/90 font-medium tracking-wide mb-4">
          We research the frontier of AI-native cyber defense.
        </p>
        <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          We publish what we learn, build what we research, and share it all —
          because the builders defending tomorrow's software need the science, not just the tooling.
        </p>

        <div className="h-px w-24 mx-auto mt-12 bg-gradient-to-r from-transparent via-saffron to-transparent" />
      </section>

      {/* ─── Why this research matters ─── */}
      <section className="relative z-10 px-5 sm:px-6 py-20 max-w-3xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-saffron mb-8 text-center">
          Why this research matters
        </h2>

        <div className="space-y-7 text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90">
          <p>
            On April 7, 2026, Anthropic unveiled{' '}
            <a
              href="https://red.anthropic.com/2026/mythos-preview/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-saffron hover:text-foreground underline underline-offset-4 decoration-saffron/40 inline-flex items-center gap-1"
            >
              Project GlassWing and Claude Mythos
              <ExternalLink className="h-4 w-4" />
            </a>
            {' '}— a frontier model that autonomously discovered zero-day vulnerabilities hidden for decades,
            built multi-stage exploit chains, and breached systems that had withstood years of manual review.
            A 27-year-old vulnerability in OpenBSD. A 17-year-old remote root in FreeBSD.
            Over 1,000 critical vulnerabilities surfaced in a single research cycle.
          </p>
          <p>
            The era of AI-native cybersecurity is here. The offense side of that equation moved fast.
            Defense has to move faster — and smarter. That means building systems with
            defense in depth and zero trust from the inside out, shifting security left into
            every build pipeline and every development workflow.
          </p>
          <p className="text-foreground font-medium">
            We come to this from 25+ years of building mission-critical systems — telecom,
            federal platforms, supply chains. That practitioner vantage point shapes every
            question we ask and every tool we build. This lab exists because the builders
            writing and deploying software in the age of agentic AI need the science to back them up.
          </p>
        </div>
      </section>

      {/* ─── Research focus areas ─── */}
      <section id="focus" className="relative z-10 px-5 sm:px-6 py-20 max-w-6xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-saffron mb-4 text-center">
          Focus areas
        </h2>
        <p className="font-display text-3xl sm:text-4xl font-semibold text-center text-foreground mb-4 leading-tight max-w-3xl mx-auto">
          Four questions driving the work
        </p>
        <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-14">
          Each area connects directly to what builders and defenders need to know and build right now.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
          {[
            {
              icon: Bot,
              title: 'Agentic Vulnerability Discovery',
              description: 'How do autonomous AI systems find, triage, and prioritize vulnerabilities at scale? We study the models, the prompting strategies, and the toolchain integrations that make this possible — and how to use them defensively.',
              accent: 'bg-saffron/15 text-saffron border-saffron/30',
              ring: 'hover:border-saffron/40',
            },
            {
              icon: Shield,
              title: 'Autonomous Defense & Remediation',
              description: 'Detection and alerting are table stakes. We research systems that can respond, contain, and remediate autonomously — closing the gap between when a threat is found and when it is resolved.',
              accent: 'bg-primary/15 text-primary-strong border-primary/30',
              ring: 'hover:border-primary/40',
            },
            {
              icon: Code2,
              title: 'Secure Agentic Development',
              description: 'When AI agents write, review, and deploy code, the security surface of the development lifecycle changes fundamentally. We study how to build safe agentic dev workflows — shift left, by design, from the first prompt.',
              accent: 'bg-india-green/15 text-india-green border-india-green/30',
              ring: 'hover:border-india-green/40',
            },
            {
              icon: Network,
              title: 'Zero Trust for the AI Era',
              description: 'Identity, least privilege, and trust boundaries when the actor is an AI agent. We research how zero trust principles apply — and need to evolve — when agents are the ones requesting access, running tools, and making decisions.',
              accent: 'bg-chakra-navy/15 text-chakra-navy border-chakra-navy/35',
              ring: 'hover:border-chakra-navy/45',
            },
          ].map((area) => (
            <div
              key={area.title}
              className={`p-7 sm:p-8 rounded-2xl bg-card border border-border transition-all hover:-translate-y-0.5 ${area.ring}`}
            >
              <div className={`h-12 w-12 rounded-xl border flex items-center justify-center mb-5 ${area.accent}`}>
                <area.icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground mb-2">{area.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Publications ─── */}
      <section id="publications" className="relative z-10 px-5 sm:px-6 py-20 bg-muted/40">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-saffron mb-4 text-center">
            Publications
          </h2>
          <p className="font-display text-3xl sm:text-4xl font-semibold text-center text-foreground mb-4 leading-tight">
            Research in progress
          </p>
          <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-14">
            These papers are in active development. We publish pre-prints and working notes as we go.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                title: 'Toward Autonomous Patch Generation for Long-Tail CVEs',
                area: 'Agentic Vulnerability Discovery',
                description: 'A framework for using frontier models to generate, validate, and stage patches for vulnerabilities with sparse prior art and no existing exploit mitigation tooling.',
                status: 'Pre-publication',
              },
              {
                title: 'Trust Boundaries in Multi-Agent Software Development',
                area: 'Zero Trust for the AI Era',
                description: 'Formalizing the identity and authorization surface that emerges when AI agents operate in collaborative coding pipelines — and the threat models that follow.',
                status: 'Working draft',
              },
              {
                title: 'Shift-Left Security Patterns for AI-Generated Code',
                area: 'Secure Agentic Development',
                description: 'A pattern library and evaluation methodology for integrating security review into AI coding agent workflows, covering static analysis, secrets detection, and dependency trust.',
                status: 'Working draft',
              },
            ].map((paper) => (
              <div
                key={paper.title}
                className="p-6 rounded-2xl bg-card border border-border flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="h-10 w-10 rounded-xl bg-saffron/10 border border-saffron/25 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-saffron" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-saffron bg-saffron/10 border border-saffron/25 px-2 py-1 rounded-full whitespace-nowrap">
                    {paper.status}
                  </span>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{paper.area}</p>
                  <h3 className="font-display text-base font-semibold text-foreground mb-2 leading-snug">{paper.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{paper.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Open source ─── */}
      <section id="open-source" className="relative z-10 px-5 sm:px-6 py-20 max-w-5xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-saffron mb-4 text-center">
          Open source
        </h2>
        <p className="font-display text-3xl sm:text-4xl font-semibold text-center text-foreground mb-4 leading-tight">
          We build what we research
        </p>
        <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-14">
          Every significant tool and methodology that comes out of the lab gets published in the open.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              title: 'Trinetra Scanner',
              description: 'The agentic vulnerability scanner at the core of the Trinetra platform. The scan engine and agent harness are open source.',
              status: 'Live',
              statusColor: 'text-india-green bg-india-green/10 border-india-green/30',
              link: 'https://trinetra.nirvik.ai',
              linkLabel: 'trinetra.nirvik.ai',
            },
            {
              title: 'MCP Security Toolkit',
              description: 'A collection of security-focused MCP servers — secrets detection, dependency auditing, and SAST integrations — for use with Claude Code, Cursor, and other MCP-compatible agents.',
              status: 'Coming soon',
              statusColor: 'text-saffron bg-saffron/10 border-saffron/25',
              link: null,
              linkLabel: null,
            },
            {
              title: 'Agent Security Harness',
              description: 'A test harness for evaluating the security posture of agentic AI workflows — covering privilege escalation, prompt injection, tool misuse, and data exfiltration scenarios.',
              status: 'Coming soon',
              statusColor: 'text-saffron bg-saffron/10 border-saffron/25',
              link: null,
              linkLabel: null,
            },
          ].map((project) => (
            <div
              key={project.title}
              className="p-6 rounded-2xl bg-card border border-border flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="h-10 w-10 rounded-xl bg-muted border border-border flex items-center justify-center shrink-0">
                  <Github className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-1 rounded-full whitespace-nowrap ${project.statusColor}`}>
                  {project.status}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-display text-base font-semibold text-foreground mb-2">{project.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{project.description}</p>
              </div>
              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-saffron hover:text-foreground transition-colors"
                >
                  {project.linkLabel}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─── Writing / blog ─── */}
      <section id="writing" className="relative z-10 px-5 sm:px-6 py-20 bg-muted/40">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-saffron mb-4 text-center">
            Writing
          </h2>
          <p className="font-display text-3xl sm:text-4xl font-semibold text-center text-foreground mb-4 leading-tight">
            Practitioner notes from the lab
          </p>
          <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-14">
            Shorter-form thinking — observations, patterns, and ideas that don't fit a paper but are too useful to keep internal.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                title: 'What April 7 Means for Every Builder',
                tag: 'Context',
                description: 'The inflection point wasn\'t just about a model capability jump. It was about the pace at which the offense side of AI security moved — and what that pace requires from defenders.',
                href: null,
                status: 'Coming soon',
                statusClass: 'text-saffron bg-saffron/10 border-saffron/25',
              },
              {
                title: 'Defense in Depth is a Systems Engineering Problem',
                tag: 'Practitioner notes',
                description: 'Twenty-five years of building mission-critical systems taught us that defense in depth isn\'t a security concept — it\'s an engineering discipline. Here\'s what that looks like in practice.',
                href: null,
                status: 'Coming soon',
                statusClass: 'text-saffron bg-saffron/10 border-saffron/25',
              },
            ].map((post) => {
              const card = (
                <div
                  className="p-6 rounded-2xl bg-card border border-border hover:border-saffron/30 transition-all hover:-translate-y-0.5 flex flex-col gap-4 h-full"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-saffron" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{post.tag}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded-full ml-auto ${post.statusClass}`}>
                      {post.status}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-foreground mb-2 leading-snug">{post.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{post.description}</p>
                  </div>
                </div>
              );
              return post.href ? (
                <a key={post.title} href={post.href} className="block h-full">
                  {card}
                </a>
              ) : (
                <div key={post.title}>{card}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative z-10 px-5 sm:px-6 py-24 max-w-3xl mx-auto text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-4">
          Follow the research
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
          We send updates when papers, tools, or significant posts ship.
          No cadence commitments — only when there's something worth reading.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center justify-center gap-2 bg-saffron text-background px-7 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-all hover:-translate-y-0.5 shadow-[0_20px_40px_-15px_color-mix(in_oklch,var(--saffron)_45%,transparent)]"
          >
            <Mail className="h-5 w-5" />
            Get in touch
          </a>
          <a
            href="https://trinetra.nirvik.ai"
            className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-7 py-3.5 rounded-xl text-base font-semibold border border-border hover:bg-accent transition-all"
          >
            Try Trinetra
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>

      {/* ─── Tricolor strip ─── */}
      <div className="relative z-10 flex">
        <div className="h-0.5 flex-1 bg-saffron" />
        <div className="h-0.5 flex-1 bg-white" />
      </div>

      {/* ─── Footer ─── */}
      <footer className="relative z-10 px-5 sm:px-6 py-10 border-t border-border/50">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <FlaskConical className="h-5 w-5 text-saffron" />
            <span className="font-display text-sm font-semibold tracking-wide text-muted-foreground">Nirvik Research</span>
            <span className="text-xs text-muted-foreground/70 ml-2">
              — A <a href="https://nirvik.ai" className="underline underline-offset-2 hover:text-foreground transition-colors">Nirvik AI Labs</a> initiative
            </span>
          </div>
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            Open research. Practical defense. &copy; 2026 Nirvik AI Labs.
          </p>
        </div>
      </footer>
    </div>
  );
}
